import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Users,
  MapPin,
  FileText,
  Plus,
  Trash2,
  Search,
  Clock,
  ChefHat,
  AlertTriangle,
  Loader2,
  Check,
  ExternalLink,
  Table2,
  CalendarDays,
} from 'lucide-react';
import clsx from 'clsx';
import { ShiftInstance, ShiftAssignment, AddAssignmentData } from '../services/api';

interface ShiftDetailPanelProps {
  instance: ShiftInstance | null;
  isOpen: boolean;
  onClose: () => void;
  onAddAssignment: (instanceId: string, data: AddAssignmentData) => Promise<void>;
  onRemoveAssignment: (instanceId: string, assignmentId: string) => Promise<void>;
  onUpdateAssignment?: (instanceId: string, assignmentId: string, data: { sectionId?: string | null; tableIds?: string[] }) => Promise<void>;
  onUpdateInstance: (instanceId: string, data: { notes?: string; status?: 'scheduled' | 'in_progress' | 'completed' | 'cancelled' }) => Promise<void>;
  onDeleteShift?: (instanceId: string) => Promise<void>;
  availableEmployees: Array<{
    id: string;
    firstName: string;
    lastName: string;
    primaryRole: string;
  }>;
  sections: Array<{ id: string; name: string; color?: string }>;
  isLoadingEmployees?: boolean;
  isDeletingShift?: boolean;
}

// Role labels
const ROLE_LABELS: Record<string, string> = {
  server: 'Server',
  bartender: 'Bartender',
  host: 'Host',
  cook: 'Cook',
  manager: 'Manager',
  busser: 'Busser',
  dishwasher: 'Dishwasher',
};

// Format time for display
function formatTime(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'UTC',
  });
}

// Format date for display (handles timezone correctly)
function formatDate(dateStr: string): string {
  // Parse as local date to avoid timezone shift
  // Input could be "2025-04-15" or "2025-04-15T00:00:00.000Z"
  const dateOnly = dateStr.split('T')[0];
  const [year, month, day] = dateOnly.split('-').map(Number);
  const date = new Date(year, month - 1, day); // month is 0-indexed
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

type TabId = 'staff' | 'sections' | 'notes';

const TABS: Array<{ id: TabId; label: string; icon: typeof Users }> = [
  { id: 'staff', label: 'Staff', icon: Users },
  { id: 'sections', label: 'Sections', icon: MapPin },
  { id: 'notes', label: 'Notes', icon: FileText },
];

export default function ShiftDetailPanel({
  instance,
  isOpen,
  onClose,
  onAddAssignment,
  onRemoveAssignment,
  onUpdateAssignment,
  onUpdateInstance,
  onDeleteShift,
  availableEmployees,
  sections,
  isLoadingEmployees,
  isDeletingShift,
}: ShiftDetailPanelProps) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>('staff');
  const [showAddStaff, setShowAddStaff] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [notes, setNotes] = useState('');
  const [isAddingStaff, setIsAddingStaff] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Reset state when panel opens/closes or instance changes
  useState(() => {
    if (instance) {
      setNotes(instance.notes || '');
    }
    setShowAddStaff(false);
    setEmployeeSearch('');
    setSelectedRole('');
  });

  if (!isOpen || !instance) return null;

  const assignments = instance.assignments || [];
  const staffingSummary = instance.staffingSummary || [];
  const totalRequired = instance.totalRequired || 0;
  const totalAssigned = instance.totalAssigned || 0;

  // Filter available employees by search and role
  const filteredEmployees = availableEmployees.filter((emp) => {
    const matchesSearch =
      !employeeSearch ||
      `${emp.firstName} ${emp.lastName}`
        .toLowerCase()
        .includes(employeeSearch.toLowerCase());
    const matchesRole = !selectedRole || emp.primaryRole === selectedRole;
    return matchesSearch && matchesRole;
  });

  // Handle adding staff
  const handleAddStaff = async (
    employeeId: string,
    role: string,
    sectionId?: string
  ) => {
    setIsAddingStaff(true);
    try {
      await onAddAssignment(instance.id, {
        employeeId,
        role,
        sectionId,
      });
      setShowAddStaff(false);
      setEmployeeSearch('');
      setSelectedRole('');
    } catch (error) {
      console.error('Failed to add staff:', error);
    } finally {
      setIsAddingStaff(false);
    }
  };

  // Handle removing staff
  const handleRemoveStaff = async (assignmentId: string) => {
    setRemovingId(assignmentId);
    try {
      await onRemoveAssignment(instance.id, assignmentId);
    } catch (error) {
      console.error('Failed to remove staff:', error);
    } finally {
      setRemovingId(null);
    }
  };

  // Handle saving notes
  const handleSaveNotes = async () => {
    try {
      await onUpdateInstance(instance.id, { notes });
    } catch (error) {
      console.error('Failed to save notes:', error);
    }
  };

  // Get missing roles
  const missingRoles = staffingSummary.filter((s) => !s.isFilled);

  return (
    <div className="fixed inset-y-0 right-0 z-50 flex">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative w-full max-w-md bg-white shadow-xl flex flex-col">
        {/* Header */}
        <div
          className="px-6 py-4 border-b border-slate-200"
          style={{ backgroundColor: `${instance.color || '#3B82F6'}15` }}
        >
          <div className="flex items-start justify-between">
            <div>
              <h2
                className="text-lg font-semibold"
                style={{ color: instance.color || '#3B82F6' }}
              >
                {instance.name}
              </h2>
              <p className="text-sm text-slate-600 mt-0.5">
                {formatDate(instance.date)} · {formatTime(instance.startTime)} -{' '}
                {formatTime(instance.endTime)}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/50 transition-colors"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          {/* Staffing summary */}
          <div className="mt-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div
                className={clsx(
                  'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium',
                  totalAssigned >= totalRequired
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-amber-100 text-amber-700'
                )}
              >
                <Users className="w-4 h-4" />
                {totalAssigned}/{totalRequired} Staff
              </div>
              {missingRoles.length > 0 && (
                <div className="text-xs text-amber-600">
                  Missing: {missingRoles.map((r) => `${r.required - r.assigned} ${ROLE_LABELS[r.role] || r.role}`).join(', ')}
                </div>
              )}
            </div>
            <button
              onClick={() => {
                onClose();
                navigate(`/shifts/${instance.id}/setup`);
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Full Setup
            </button>
          </div>

          {/* Event notes banner */}
          {instance.notes && (
            <div
              className={clsx(
                'mt-3 px-3 py-2 rounded-lg text-sm',
                instance.notes.includes('NEED') || instance.notes.includes('?')
                  ? 'bg-amber-100 border border-amber-300 text-amber-800'
                  : 'bg-blue-100 border border-blue-300 text-blue-800'
              )}
            >
              <div className="flex items-start gap-2">
                {instance.notes.includes('NEED') || instance.notes.includes('?') ? (
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-600" />
                ) : (
                  <CalendarDays className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-600" />
                )}
                <span className="leading-snug font-medium">{instance.notes}</span>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 bg-slate-50/50">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={clsx(
                'flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors',
                activeTab === id
                  ? 'text-indigo-600 border-b-2 border-indigo-600 bg-white'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
              )}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'staff' && (
            <StaffTab
              assignments={assignments}
              staffingSummary={staffingSummary}
              showAddStaff={showAddStaff}
              setShowAddStaff={setShowAddStaff}
              employeeSearch={employeeSearch}
              setEmployeeSearch={setEmployeeSearch}
              selectedRole={selectedRole}
              setSelectedRole={setSelectedRole}
              filteredEmployees={filteredEmployees}
              sections={sections}
              isLoadingEmployees={isLoadingEmployees}
              isAddingStaff={isAddingStaff}
              removingId={removingId}
              onAddStaff={handleAddStaff}
              onRemoveStaff={handleRemoveStaff}
              onUpdateSection={onUpdateAssignment ? async (assignmentId, sectionId) => {
                await onUpdateAssignment(instance.id, assignmentId, { sectionId });
              } : undefined}
              onUpdateTables={onUpdateAssignment ? async (assignmentId, tableIds) => {
                await onUpdateAssignment(instance.id, assignmentId, { tableIds });
              } : undefined}
            />
          )}

          {activeTab === 'sections' && (
            <SectionsTab
              activeSectionIds={instance.activeSectionIds}
              sections={sections}
              assignments={assignments}
            />
          )}

          {activeTab === 'notes' && (
            <NotesTab
              notes={notes}
              setNotes={setNotes}
              onSave={handleSaveNotes}
            />
          )}
        </div>

        {/* Footer with delete */}
        {onDeleteShift && (
          <div className="px-4 py-3 border-t border-slate-200 bg-slate-50">
            {!showDeleteConfirm ? (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center justify-center gap-2 w-full py-2 text-sm font-medium text-red-600 border border-red-200 bg-white rounded-lg hover:bg-red-50 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete Shift
              </button>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-slate-600 text-center">
                  Are you sure you want to delete this shift?
                </p>
                {assignments.length > 0 && (
                  <p className="text-xs text-red-600 text-center font-medium">
                    {assignments.length} staff member{assignments.length === 1 ? '' : 's'} will be unassigned
                    {instance.published ? ' and notified that their shift was cancelled.' : '.'}
                  </p>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={isDeletingShift}
                    className="flex-1 py-2 text-sm font-medium text-slate-600 border border-slate-200 bg-white rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => onDeleteShift(instance.id)}
                    disabled={isDeletingShift}
                    className="flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {isDeletingShift ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Staff Tab Component
interface StaffTabProps {
  assignments: ShiftAssignment[];
  staffingSummary: Array<{ role: string; required: number; assigned: number; isFilled: boolean }>;
  showAddStaff: boolean;
  setShowAddStaff: (show: boolean) => void;
  employeeSearch: string;
  setEmployeeSearch: (search: string) => void;
  selectedRole: string;
  setSelectedRole: (role: string) => void;
  filteredEmployees: Array<{ id: string; firstName: string; lastName: string; primaryRole: string }>;
  sections: Array<{ id: string; name: string; color?: string }>;
  isLoadingEmployees?: boolean;
  isAddingStaff: boolean;
  removingId: string | null;
  onAddStaff: (employeeId: string, role: string, sectionId?: string) => void;
  onRemoveStaff: (assignmentId: string) => void;
  onUpdateSection?: (assignmentId: string, sectionId: string | null) => void;
  onUpdateTables?: (assignmentId: string, tableIds: string[]) => void;
}

function StaffTab({
  assignments,
  staffingSummary,
  showAddStaff,
  setShowAddStaff,
  employeeSearch,
  setEmployeeSearch,
  selectedRole,
  setSelectedRole,
  filteredEmployees,
  sections,
  isLoadingEmployees,
  isAddingStaff,
  removingId,
  onAddStaff,
  onRemoveStaff,
  onUpdateSection,
  onUpdateTables,
}: StaffTabProps & { onUpdateTables?: (assignmentId: string, tableIds: string[]) => void }) {
  const [addingEmployeeId, setAddingEmployeeId] = useState<string | null>(null);
  const [addSectionId, setAddSectionId] = useState<string>('');
  const [updatingSectionId, setUpdatingSectionId] = useState<string | null>(null);
  const [updatingTablesId, setUpdatingTablesId] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      {/* Staffing requirements summary */}
      <div className="p-3 bg-slate-50 rounded-lg">
        <h4 className="text-xs font-medium text-slate-500 uppercase mb-2">
          Requirements
        </h4>
        <div className="space-y-1">
          {staffingSummary.map((req) => (
            <div
              key={req.role}
              className="flex items-center justify-between text-sm"
            >
              <span className="text-slate-700">
                {ROLE_LABELS[req.role] || req.role}
              </span>
              <span
                className={clsx(
                  'font-medium',
                  req.isFilled ? 'text-emerald-600' : 'text-amber-600'
                )}
              >
                {req.assigned}/{req.required}
                {req.isFilled && <Check className="w-3 h-3 inline ml-1" />}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Assigned staff list */}
      <div>
        <h4 className="text-xs font-medium text-slate-500 uppercase mb-2">
          Assigned Staff ({assignments.length})
        </h4>

        {assignments.length === 0 ? (
          <p className="text-sm text-slate-500 italic py-2">
            No staff assigned yet
          </p>
        ) : (
          <div className="space-y-2">
            {assignments.map((assignment) => {
              const isFOH = ['server', 'bartender', 'host'].includes(assignment.role);
              const handleSectionChange = async (newSectionId: string) => {
                if (!onUpdateSection) return;
                setUpdatingSectionId(assignment.id);
                try {
                  await onUpdateSection(assignment.id, newSectionId || null);
                } finally {
                  setUpdatingSectionId(null);
                }
              };

              return (
                <div
                  key={assignment.id}
                  className="p-3 bg-white border border-slate-200 rounded-lg"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                        {assignment.role === 'cook' ? (
                          <ChefHat className="w-4 h-4 text-slate-500" />
                        ) : (
                          <Users className="w-4 h-4 text-slate-500" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">
                          {assignment.employee?.firstName} {assignment.employee?.lastName}
                        </p>
                        <p className="text-xs text-slate-500">
                          {ROLE_LABELS[assignment.role] || assignment.role}
                          {assignment.station && ` · ${assignment.station}`}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => onRemoveStaff(assignment.id)}
                      disabled={removingId === assignment.id}
                      className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {removingId === assignment.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </div>

                  {/* Section selector for FOH roles */}
                  {isFOH && sections.length > 0 && onUpdateSection && (
                    <div className="mt-2 flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-slate-400" />
                      <select
                        value={assignment.sectionId || ''}
                        onChange={(e) => handleSectionChange(e.target.value)}
                        disabled={updatingSectionId === assignment.id}
                        className="flex-1 px-2 py-1 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none disabled:opacity-50"
                      >
                        <option value="">No Section</option>
                        {sections.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                      {updatingSectionId === assignment.id && (
                        <Loader2 className="w-3 h-3 animate-spin text-indigo-600" />
                      )}
                    </div>
                  )}

                  {/* Table assignment for FOH roles with a section assigned */}
                  {isFOH && assignment.sectionId && onUpdateTables && (() => {
                    const assignedSection = sections.find(s => s.id === assignment.sectionId);
                    const sectionTables = (assignedSection as any)?.tables || [];
                    const currentTableIds = (assignment as any).tableIds || [];

                    if (sectionTables.length === 0) return null;

                    const handleTableToggle = async (tableId: string) => {
                      setUpdatingTablesId(assignment.id);
                      try {
                        const newTableIds = currentTableIds.includes(tableId)
                          ? currentTableIds.filter((id: string) => id !== tableId)
                          : [...currentTableIds, tableId];
                        await onUpdateTables(assignment.id, newTableIds);
                      } finally {
                        setUpdatingTablesId(null);
                      }
                    };

                    return (
                      <div className="mt-2">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <Table2 className="w-3.5 h-3.5 text-slate-400" />
                          <span className="text-[10px] text-slate-500 uppercase font-medium">Assigned Tables</span>
                          {updatingTablesId === assignment.id && (
                            <Loader2 className="w-3 h-3 animate-spin text-indigo-600" />
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {sectionTables.map((table: any) => {
                            const isSelected = currentTableIds.includes(table.id);
                            return (
                              <button
                                key={table.id}
                                onClick={() => handleTableToggle(table.id)}
                                disabled={updatingTablesId === assignment.id}
                                className={clsx(
                                  'px-2 py-0.5 text-[10px] rounded-md border transition-colors disabled:opacity-50',
                                  isSelected
                                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700'
                                    : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                                )}
                              >
                                T{table.number}
                              </button>
                            );
                          })}
                        </div>
                        {currentTableIds.length === 0 && (
                          <p className="text-[10px] text-slate-400 mt-1">Click tables to assign</p>
                        )}
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add staff section */}
      {!showAddStaff ? (
        <button
          onClick={() => setShowAddStaff(true)}
          className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-indigo-600 border border-dashed border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Staff
        </button>
      ) : (
        <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-slate-900">Add Staff</h4>
            <button
              onClick={() => setShowAddStaff(false)}
              className="p-1 hover:bg-slate-200 rounded transition-colors"
            >
              <X className="w-4 h-4 text-slate-500" />
            </button>
          </div>

          {/* Search and filter */}
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={employeeSearch}
                onChange={(e) => setEmployeeSearch(e.target.value)}
                placeholder="Search employees..."
                className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
              />
            </div>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
            >
              <option value="">All Roles</option>
              {Object.entries(ROLE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {/* Employee list */}
          {isLoadingEmployees ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : filteredEmployees.length === 0 ? (
            <p className="text-sm text-slate-500 italic py-2 text-center">
              No available employees found
            </p>
          ) : (
            <div className="max-h-48 overflow-y-auto space-y-1">
              {filteredEmployees.map((emp) => (
                <div
                  key={emp.id}
                  className="flex items-center justify-between p-2 hover:bg-white rounded-lg transition-colors"
                >
                  <div>
                    <p className="text-sm font-medium text-slate-900">
                      {emp.firstName} {emp.lastName}
                    </p>
                    <p className="text-xs text-slate-500">
                      {ROLE_LABELS[emp.primaryRole] || emp.primaryRole}
                    </p>
                  </div>
                  {addingEmployeeId === emp.id ? (
                    <div className="flex items-center gap-2">
                      {['server', 'bartender', 'host'].includes(emp.primaryRole) && sections.length > 0 && (
                        <select
                          value={addSectionId}
                          onChange={(e) => setAddSectionId(e.target.value)}
                          className="px-2 py-1 text-xs border border-slate-200 rounded-lg"
                        >
                          <option value="">No Section</option>
                          {sections.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      )}
                      <button
                        onClick={() => {
                          onAddStaff(emp.id, emp.primaryRole, addSectionId || undefined);
                          setAddingEmployeeId(null);
                          setAddSectionId('');
                        }}
                        disabled={isAddingStaff}
                        className="px-3 py-1 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {isAddingStaff ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          'Add'
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setAddingEmployeeId(null);
                          setAddSectionId('');
                        }}
                        className="p-1 hover:bg-slate-200 rounded"
                      >
                        <X className="w-3 h-3 text-slate-500" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setAddingEmployeeId(emp.id)}
                      className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Missing staff warning */}
      {staffingSummary.some((s) => !s.isFilled) && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-700">
            <p className="font-medium">Missing staff</p>
            <p className="text-xs mt-0.5">
              {staffingSummary
                .filter((s) => !s.isFilled)
                .map((s) => `${s.required - s.assigned} ${ROLE_LABELS[s.role] || s.role}`)
                .join(', ')}{' '}
              still needed
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// Sections Tab Component
interface TableData {
  id: string;
  number: string;
  name?: string;
  capacity: number;
  status?: string;
}

interface SectionWithTables {
  id: string;
  name: string;
  color?: string;
  tables?: TableData[];
}

interface SectionsTabProps {
  activeSectionIds: string[];
  sections: Array<SectionWithTables>;
  assignments: ShiftAssignment[];
}

function SectionsTab({ activeSectionIds, sections, assignments }: SectionsTabProps) {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const activeSections = sections.filter((s) => activeSectionIds.includes(s.id));
  const inactiveSections = sections.filter((s) => !activeSectionIds.includes(s.id));

  // Map assignments by section
  const assignmentsBySection: Record<string, ShiftAssignment[]> = {};
  for (const assignment of assignments) {
    if (assignment.sectionId) {
      if (!assignmentsBySection[assignment.sectionId]) {
        assignmentsBySection[assignment.sectionId] = [];
      }
      assignmentsBySection[assignment.sectionId].push(assignment);
    }
  }

  return (
    <div className="space-y-4">
      {/* Active sections */}
      <div>
        <h4 className="text-xs font-medium text-slate-500 uppercase mb-2">
          Open Sections ({activeSections.length})
        </h4>
        {activeSections.length === 0 ? (
          <p className="text-sm text-slate-500 italic py-2">
            No sections configured for this shift
          </p>
        ) : (
          <div className="space-y-2">
            {activeSections.map((section) => {
              const sectionAssignments = assignmentsBySection[section.id] || [];
              const tables = section.tables || [];
              const isExpanded = expandedSection === section.id;

              return (
                <div
                  key={section.id}
                  className="bg-white border border-slate-200 rounded-lg overflow-hidden"
                >
                  {/* Section Header */}
                  <button
                    onClick={() => setExpandedSection(isExpanded ? null : section.id)}
                    className="w-full p-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: section.color || '#3B82F6' }}
                      />
                      <span className="font-medium text-slate-900">
                        {section.name}
                      </span>
                      <span className="text-xs text-slate-400">
                        ({tables.length} tables)
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {sectionAssignments.length > 0 && (
                        <div className="flex -space-x-1">
                          {sectionAssignments.slice(0, 3).map((a) => (
                            <div
                              key={a.id}
                              className="w-6 h-6 rounded-full bg-indigo-100 border-2 border-white flex items-center justify-center text-[10px] font-medium text-indigo-600"
                              title={`${a.employee?.firstName} ${a.employee?.lastName}`}
                            >
                              {a.employee?.firstName?.charAt(0)}{a.employee?.lastName?.charAt(0)}
                            </div>
                          ))}
                          {sectionAssignments.length > 3 && (
                            <div className="w-6 h-6 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center text-[10px] font-medium text-slate-500">
                              +{sectionAssignments.length - 3}
                            </div>
                          )}
                        </div>
                      )}
                      <svg
                        className={clsx(
                          'w-4 h-4 text-slate-400 transition-transform',
                          isExpanded && 'rotate-180'
                        )}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>

                  {/* Expanded Content - Tables & Staff */}
                  {isExpanded && (
                    <div className="border-t border-slate-100 p-3 bg-slate-50/50">
                      {/* Assigned Staff */}
                      {sectionAssignments.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs font-medium text-slate-500 mb-1.5">Assigned Staff</p>
                          <div className="flex flex-wrap gap-1.5">
                            {sectionAssignments.map((a) => (
                              <span
                                key={a.id}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full"
                                style={{
                                  backgroundColor: `${section.color || '#3B82F6'}15`,
                                  color: section.color || '#3B82F6',
                                }}
                              >
                                <Users className="w-3 h-3" />
                                {a.employee?.firstName} {a.employee?.lastName?.charAt(0)}.
                                <span className="text-[10px] opacity-70">({ROLE_LABELS[a.role] || a.role})</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Tables Grid */}
                      {tables.length > 0 ? (
                        <div>
                          <p className="text-xs font-medium text-slate-500 mb-1.5">Tables</p>
                          <div className="grid grid-cols-4 gap-2">
                            {tables.map((table) => (
                              <div
                                key={table.id}
                                className={clsx(
                                  'relative p-2 rounded-lg border text-center transition-all',
                                  table.status === 'occupied'
                                    ? 'bg-amber-50 border-amber-200'
                                    : table.status === 'reserved'
                                    ? 'bg-blue-50 border-blue-200'
                                    : 'bg-white border-slate-200 hover:border-slate-300'
                                )}
                              >
                                <div className="text-sm font-semibold text-slate-800">
                                  {table.number}
                                </div>
                                <div className="text-[10px] text-slate-500">
                                  {table.capacity} seats
                                </div>
                                {table.status && table.status !== 'available' && (
                                  <div className={clsx(
                                    'absolute -top-1 -right-1 w-3 h-3 rounded-full border border-white',
                                    table.status === 'occupied' ? 'bg-amber-400' : 'bg-blue-400'
                                  )} />
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400 italic">No tables in this section</p>
                      )}

                      {sectionAssignments.length === 0 && (
                        <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-xs text-amber-700 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            No staff assigned to this section
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Unassigned Staff Warning */}
      {assignments.filter(a => !a.sectionId && ['server', 'bartender', 'host'].includes(a.role)).length > 0 && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-xs font-medium text-amber-700 mb-1">FOH Staff Without Section</p>
          <div className="flex flex-wrap gap-1">
            {assignments
              .filter(a => !a.sectionId && ['server', 'bartender', 'host'].includes(a.role))
              .map((a) => (
                <span
                  key={a.id}
                  className="px-2 py-0.5 text-xs bg-white text-amber-700 rounded-full border border-amber-200"
                >
                  {a.employee?.firstName} {a.employee?.lastName?.charAt(0)}. ({ROLE_LABELS[a.role]})
                </span>
              ))}
          </div>
        </div>
      )}

      {/* Inactive sections */}
      {inactiveSections.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-slate-500 uppercase mb-2">
            Closed Sections ({inactiveSections.length})
          </h4>
          <div className="flex flex-wrap gap-2">
            {inactiveSections.map((section) => (
              <span
                key={section.id}
                className="px-3 py-1.5 text-sm text-slate-400 bg-slate-100 rounded-lg"
              >
                {section.name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Notes Tab Component
interface NotesTabProps {
  notes: string;
  setNotes: (notes: string) => void;
  onSave: () => void;
}

function NotesTab({ notes, setNotes, onSave }: NotesTabProps) {
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-slate-500 uppercase mb-2">
          Shift Notes
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add notes about this shift..."
          rows={6}
          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-none"
        />
      </div>
      <button
        onClick={handleSave}
        disabled={isSaving}
        className="flex items-center justify-center gap-2 w-full py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
      >
        {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
        Save Notes
      </button>
    </div>
  );
}
